import React, {
  useState,
  useRef,
  useEffect,
  useContext,
  useCallback,
} from "react";
import { deepCopy } from "../../leftSide/Models/modelLogic";
import {
  adjustModalPositionAndSize,
  hasProperty,
} from "../../../utils/helpers";
import Modal from "react-modal";
// import { Button } from "@mui/material";
import deleteIcon from "../../../res/icons/delete.png";
//import Downloasocon from "@mui/icons-material/Download";
import "./windowComps.scss";
import ColorPicker from "./ColorPicker";
import LineStyleSelect from "./LineStyleSelect";
import LineWidthSelect from "./LineWidthSelect";
import LineModeSelect from "./LineModeSelect";
import MarkerSizeSelect from "./MarkerSizeSelect";
import MarkerSymbolSelect from "./MarkerSymbolSelect";
import { GeneralContext } from "../../../context/GeneralContext";
import CustomButton from "../../commonComponents/CustomButton";
import CloseButton from "../../commonComponents/CloseButton";
import HtmlTooltip from "../../commonComponents/HtmlTooltip";

function CurvesModal(props) {
  const {
    curvesModalIsOpen,
    setCurvesModalIsOpen,
    reference,
    distList,
    handleDistDelete,
    selectFileList,
    selectModelList,
    graphRef,
    id,
    updateGraphPlots,
    zIndex,
    type = "standard",
  } = props;

  const { recordedErrorLog } = useContext(GeneralContext);

  const [isDistOverlayVisible, setIsDistOverlayVisible] = useState(false);
  const [colorModalOpen, setColorModalOpen] = useState(false);
  const [colorToChange, setColorToChange] = useState(null);
  const [curveToChange, setCurveToChange] = useState(null);
  const [clickCoordinates, setClickCoordinates] = useState({ x: 0, y: 0 });
  const [updatedPlots, setUpdatedPlots] = useState([]);
  const distListRef = useRef(null);
  const [curvesModalPlaceAndSize, setCurvesModalPlaceAndSize] = useState({
    top: "0",
    left: "0",
    height: "300px",
    width: "300px",
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const modalRef = useRef(null);
  const prevModalOpenRef = useRef(false);

  useEffect(() => {
    try {
      if (curvesModalIsOpen) {
        const modalPosition = {
          top:
            reference.current != null
              ? reference.current.getBoundingClientRect().top
              : 0,
          right: "auto",
          left:
            reference.current != null
              ? reference.current.getBoundingClientRect().left
              : 0,
        };
        const modalSize = { width: 700, height: 500 };

        const adjusted = adjustModalPositionAndSize(modalPosition, modalSize);

        setCurvesModalPlaceAndSize(adjusted);

        // Automatically open overlay if modal just opened and there are no curves
        const modalJustOpened = !prevModalOpenRef.current;
        if (modalJustOpened && distList.length === 0 && type === "standard") {
          setIsDistOverlayVisible(true);
        }
      }

      // Update ref to track previous state
      prevModalOpenRef.current = curvesModalIsOpen;
    } catch (error) {
      recordedErrorLog("Error initializing curves modal:", error);
    }
  }, [curvesModalIsOpen, distList.length, type]);

  useEffect(() => {
    try {
      updatePlots();
    } catch (error) {
      recordedErrorLog("Error updating plots:", error);
    }
  }, [updatedPlots]);

  const getGraphName = () => {
    try {
      const currentGraph = graphRef.current.find((graph) => graph.id == id);
      if (currentGraph) {
        return ": " + currentGraph.title;
      }
      return " ";
    } catch (error) {
      recordedErrorLog("Error getting graph name:", error);
      return " ";
    }
  };

  const handleCloseSettingsModal = () => {
    try {
      updatePlots();

      setCurvesModalIsOpen(false);
      setIsDistOverlayVisible(false);
    } catch (error) {
      recordedErrorLog("Settings modal closing handler failure: ", error);
    }
  };

  // Drag handlers
  const handleDragStart = (e) => {
    // Don't start drag if clicking on buttons or interactive elements
    if (
      e.target.closest("button") ||
      e.target.closest(".curvesModalHeaderButtons") ||
      e.target.closest("input") ||
      e.target.closest("select")
    ) {
      return;
    }

    if (modalRef.current) {
      const rect = modalRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
      setIsDragging(true);
      e.preventDefault();
    }
  };

  const handleDrag = useCallback(
    (e) => {
      if (modalRef.current) {
        const newLeft = e.clientX - dragOffset.x;
        const newTop = e.clientY - dragOffset.y;

        // Keep modal within viewport bounds
        const maxLeft = window.innerWidth - modalRef.current.offsetWidth;
        const maxTop = window.innerHeight - modalRef.current.offsetHeight;

        const constrainedLeft = Math.max(0, Math.min(newLeft, maxLeft));
        const constrainedTop = Math.max(0, Math.min(newTop, maxTop));

        setCurvesModalPlaceAndSize((prev) => ({
          ...prev,
          left: `${constrainedLeft}px`,
          top: `${constrainedTop}px`,
          right: "auto",
        }));
      }
    },
    [dragOffset]
  );

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add global mouse event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      const handleMouseMove = (e) => {
        e.preventDefault();
        handleDrag(e);
      };
      const handleMouseUp = () => {
        handleDragEnd();
      };

      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);

      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, handleDrag, handleDragEnd]);

  const updatePlots = () => {
    try {
      const currentGraph = graphRef.current.find((graph) => graph.id == id);
      let plotsUpdate = currentGraph.plotData;

      if (updatedPlots.length > 0) {
        for (let i = 0; i < updatedPlots.length; i++) {
          const update = updatedPlots[i];
          const isModel = hasProperty(update, "modelId");
          if (isModel) {
            plotsUpdate = plotsUpdate.map((plot) => {
              let plotToReturn = plot;
              if (
                hasProperty(plot, "modelId") &&
                plot.modelId === update.modelId &&
                plot.quantity === update.quantity &&
                hasProperty(update, "color")
              ) {
                plotToReturn = {
                  ...plotToReturn,
                  line: { ...plotToReturn.line, color: update.color },
                };
              }
              if (
                hasProperty(plot, "modelId") &&
                plot.modelId === update.modelId &&
                plot.quantity === update.quantity &&
                hasProperty(update, "line")
              ) {
                plotToReturn = {
                  ...plotToReturn,
                  line: { ...plotToReturn.line, dash: update.line },
                };
              }
              if (
                hasProperty(plot, "modelId") &&
                plot.modelId === update.modelId &&
                plot.quantity === update.quantity &&
                hasProperty(update, "width")
              ) {
                plotToReturn = {
                  ...plotToReturn,
                  line: { ...plotToReturn.line, width: update.width },
                };
              }
              if (
                hasProperty(plot, "modelId") &&
                plot.modelId === update.modelId &&
                plot.quantity === update.quantity &&
                hasProperty(update, "mode")
              ) {
                plotToReturn = {
                  ...plotToReturn,
                  mode: update.mode,
                };
              }
              if (
                hasProperty(plot, "modelId") &&
                plot.modelId === update.modelId &&
                plot.quantity === update.quantity &&
                (hasProperty(update, "markerSize") ||
                  hasProperty(update, "markerSymbol"))
              ) {
                const currentMarker = plotToReturn.marker || {};
                plotToReturn = {
                  ...plotToReturn,
                  marker: {
                    ...currentMarker,
                    ...(hasProperty(update, "markerSize") && {
                      size: update.markerSize,
                    }),
                    ...(hasProperty(update, "markerSymbol") && {
                      symbol: update.markerSymbol,
                    }),
                  },
                };
              }
              return plotToReturn;
            });
          } else {
            plotsUpdate = plotsUpdate.map((plot) => {
              let plotToReturn = plot;
              if (
                hasProperty(plot, "fileId") &&
                plot.fileId === update.fileId &&
                hasProperty(update, "color")
              ) {
                plotToReturn = {
                  ...plotToReturn,
                  line: { ...plotToReturn.line, color: update.color },
                };
              }
              if (
                hasProperty(plot, "fileId") &&
                plot.fileId === update.fileId &&
                hasProperty(update, "line")
              ) {
                plotToReturn = {
                  ...plotToReturn,
                  line: { ...plotToReturn.line, dash: update.line },
                };
              }
              if (
                hasProperty(plot, "fileId") &&
                plot.fileId === update.fileId &&
                hasProperty(update, "width")
              ) {
                plotToReturn = {
                  ...plotToReturn,
                  line: { ...plotToReturn.line, width: update.width },
                };
              }
              if (
                hasProperty(plot, "fileId") &&
                plot.fileId === update.fileId &&
                hasProperty(update, "mode")
              ) {
                plotToReturn = {
                  ...plotToReturn,
                  mode: update.mode,
                };
              }
              if (
                hasProperty(plot, "fileId") &&
                plot.fileId === update.fileId &&
                (hasProperty(update, "markerSize") ||
                  hasProperty(update, "markerSymbol"))
              ) {
                const currentMarker = plotToReturn.marker || {};
                plotToReturn = {
                  ...plotToReturn,
                  marker: {
                    ...currentMarker,
                    ...(hasProperty(update, "markerSize") && {
                      size: update.markerSize,
                    }),
                    ...(hasProperty(update, "markerSymbol") && {
                      symbol: update.markerSymbol,
                    }),
                  },
                };
              }
              return plotToReturn;
            });
          }
        }
        updateGraphPlots(plotsUpdate);
      }
    } catch (error) {
      recordedErrorLog("Error updating plots:", error);
    }
  };

  const toggleDistOverlay = () => {
    try {
    setIsDistOverlayVisible(!isDistOverlayVisible);
    } catch (error) {
      recordedErrorLog("Error toggling dist overlay:", error);
    }
  };

  const handleDistAdd = () => {
    try {
    toggleDistOverlay();
    } catch (error) {
      recordedErrorLog("Error handling dist add:", error);
    }
  };

  const handleColorClick = (currentColor, dist, e) => {
    try {
    setClickCoordinates({
      x: e.clientX,
      y: e.clientY,
    });
    setColorToChange(currentColor);
    setCurveToChange(dist);
    setColorModalOpen(true);
    } catch (error) {
      recordedErrorLog("Error handling color click:", error);
    }
  };

  const handleColorModalClose = () => {
    try {
    setColorModalOpen(false);
    } catch (error) {
      recordedErrorLog("Error handling color modal close:", error);
    }
  };

  const handleNewColorConfirm = (color) => {
    try {
    setColorModalOpen(false);
    updatePlotsWithVal({ color: color }, curveToChange);
    } catch (error) {
      recordedErrorLog("Error handling new color confirm:", error);
    }
  };

  function findCurveColor(curve, type) {
    try {
      if (curve === undefined) {
        return "#000";
      }

      if (updatedPlots.length === 0) {
        return curve.line.color;
      }

      if (type === "model") {
        for (let i = 0; i < updatedPlots.length; i++) {
          const update = updatedPlots[i];
          if (
            hasProperty(update, "modelId") &&
            update.modelId === curve.modelId &&
            update.quantity === curve.quantity &&
            hasProperty(update, "color")
          ) {
            return update.color;
          }
        }
      } else if (type === "file") {
        for (let i = 0; i < updatedPlots.length; i++) {
          const update = updatedPlots[i];
          if (
            hasProperty(update, "fileId") &&
            update.fileId === curve.fileId &&
            hasProperty(update, "color")
          ) {
            return update.color;
          }
        }
      } else {
        for (let i = 0; i < updatedPlots.length; i++) {
          const update = updatedPlots[i];
          if (
            hasProperty(update, "variableId") &&
            update.variableId === curve.variableId &&
            hasProperty(update, "color")
          ) {
            return update.color;
          }
        }
      }

      // Updated color not found, so we return the original color
      return curve.line.color;
    } catch (error) {
      recordedErrorLog("Curve color finder failure: ", error);
    }
  }

  const updateLine = (value, curve) => {
    try {
    updatePlotsWithVal({ line: value }, curve);
    } catch (error) {
      recordedErrorLog("Error updating line:", error);
    }
  };

  const updateWidth = (value, curve) => {
    try {
    updatePlotsWithVal({ width: value }, curve);
    } catch (error) {
      recordedErrorLog("Error updating width:", error);
    }
  };

  const updateLineMode = (value, curve) => {
    try {
    updatePlotsWithVal({ mode: value }, curve);
    } catch (error) {
      recordedErrorLog("Error updating line mode:", error);
    }
  };

  const updateMarkerSize = (value, curve) => {
    try {
    updatePlotsWithVal({ markerSize: value }, curve);
    } catch (error) {
      recordedErrorLog("Error updating marker size:", error);
    }
  };

  const updateMarkerSymbol = (value, curve) => {
    try {
    updatePlotsWithVal({ markerSymbol: value }, curve);
    } catch (error) {
      recordedErrorLog("Error updating marker symbol:", error);
    }
  };

  const updatePlotsWithVal = (valObj, curve) => {
    try {
      const plotUpdates = deepCopy(updatedPlots);
      const isModel = hasProperty(curve, "modelId");

      let replaced = false;
      for (let i = 0; i < plotUpdates.length; i++) {
        const update = plotUpdates[i];
        if (
          isModel &&
          hasProperty(update, "modelId") &&
          update.modelId === curve.modelId &&
          update.quantity === curve.quantity
        ) {
          plotUpdates[i] = {
            ...plotUpdates[i],
            ...valObj,
            modelId: curve.modelId,
          };
          replaced = true;
          break;
        } else if (
          !isModel &&
          hasProperty(update, "fileId") &&
          update.fileId === curve.fileId
        ) {
          plotUpdates[i] = {
            ...plotUpdates[i],
            ...valObj,
            fileId: curve.fileId,
          };
          replaced = true;
          break;
        }
      }

      if (!replaced) {
        if (isModel) {
          plotUpdates.push({
            ...valObj,
            modelId: curve.modelId,
            quantity: curve.quantity,
          });
        } else {
          plotUpdates.push({
            ...valObj,
            fileId: curve.fileId,
          });
        }
      }

      setUpdatedPlots(plotUpdates);
    } catch (error) {
      recordedErrorLog("Plot value updater failure: ", error);
    }
  };

  return (
    <Modal
      isOpen={curvesModalIsOpen}
      onRequestClose={handleCloseSettingsModal}
      shouldCloseOnOverlayClick={true}
      contentLabel="Model Modal"
      appElement={reference.current}
      style={{
        content: {
          width: curvesModalPlaceAndSize.width,
          height: curvesModalPlaceAndSize.height,
          top: curvesModalPlaceAndSize.top,
          left: curvesModalPlaceAndSize.left,
          right: curvesModalPlaceAndSize.right,
          border: "1px solid black",
          borderRadius: "10px",
          padding: "0",
        },
        overlay: {
          backgroundColor: "transparent",
          zIndex: zIndex + 200,
        },
      }}
      id="grahp-curves-modal"
    >
      <div className="curvesModal" id="graph-curves-modal" ref={modalRef}>
        <div
          className="curvesModalHeader"
          onMouseDown={handleDragStart}
          style={{ cursor: isDragging ? "grabbing" : "grab" }}
        >
          <div className="curvesModalTitle">Curves List{getGraphName()}</div>
          <div className="curvesModalHeaderButtons">
            {type === "standard" && (
              <CustomButton
                text={isDistOverlayVisible ? "Curves List" : "Add Curves"}
                extraClassnames={["noMargin"]}
                handleClick={handleDistAdd}
                id="add-curves-button-curve-modal"
              />
            )}
            <CloseButton onClose={handleCloseSettingsModal} />
          </div>
        </div>
        <div className="curvesModalContent">
          {distList.length > 0 ? (
            <>
              <div className="distListHeader">
                <div className="distListHeaderEntry">Name</div>
                <div className="distListHeaderEntry">Color</div>
                <div className="distListHeaderEntry">Mode</div>
                <div className="distListHeaderEntry">Style</div>
                <div className="distListHeaderEntry">Width</div>
                <div className="distListHeaderEntry">Symbol</div>
                <div className="distListHeaderEntry">Size</div>
              </div>
              <div
                className="distList"
                ref={distListRef}
                id="dist-list-in-curves-modal"
              >
                {distList.map((dist) => {
                  const isModel = hasProperty(dist, "modelId");
                  const isFile = hasProperty(dist, "fileId");
                  const isVariable = hasProperty(dist, "variableId");

                  let key = "";
                  let type = "";
                  if (isModel) {
                    key = `model-${dist.modelId}-${dist.quantity}`;
                    type = "model";
                  }
                  if (isFile) {
                    key = `file-${dist.fileId}`;
                    type = "file";
                  }
                  if (isVariable) {
                    key = `variable-${dist.variableId}`;
                    type = "variable";
                  }

                  const currentGraph = graphRef.current.find(
                    (graph) => graph.id == id
                  );

                  const currentCurve = currentGraph.plotData.find((plot) => {
                    if (isModel) {
                      return (
                        plot.modelId === dist.modelId &&
                        plot.quantity === dist.quantity
                      );
                    } else if (isFile) {
                      return plot.fileId === dist.fileId;
                    } else {
                      return plot.variableId === dist.variableId;
                    }
                  });

                  let currentLine = "solid";
                  if (currentCurve && hasProperty(currentCurve.line, "dash")) {
                    currentLine = currentCurve.line.dash;
                  }

                  let currentWidth = 2;
                  if (currentCurve && hasProperty(currentCurve.line, "width")) {
                    currentWidth = currentCurve.line.width;
                  }

                  let currentMode = "lines";
                  if (currentCurve && hasProperty(currentCurve, "mode")) {
                    currentMode = currentCurve.mode;
                  }

                  let currentMarker = "circle";
                  if (
                    currentCurve &&
                    hasProperty(currentCurve, "marker") &&
                    hasProperty(currentCurve.marker, "symbol")
                  ) {
                    currentMarker = currentCurve.marker.symbol;
                  }

                  let currentMarkerSize = 6;
                  if (
                    currentCurve &&
                    hasProperty(currentCurve, "marker") &&
                    hasProperty(currentCurve.marker, "size")
                  ) {
                    currentMarkerSize = currentCurve.marker.size;
                  }

                  const color = findCurveColor(currentCurve, type);

                  return (
                    <div
                      key={key}
                      className="distListEntry"
                      id={`dist-entry-${key}`}
                    >
                      {isModel ? (
                        <div className="modelNameArea">
                          {(() => {
                            // Split at the last occurrence of ":"
                            const lastColonIndex = dist.name.lastIndexOf(":");
                            if (lastColonIndex !== -1) {
                              const modelName = dist.name.substring(
                                0,
                                lastColonIndex + 1
                              );
                              const quantityName = dist.name
                                .substring(lastColonIndex + 1)
                                .trim();
                              // Strip HTML tags for tooltip text
                              const modelNameText = modelName.replace(
                                /<[^>]*>/g,
                                ""
                              );
                              const quantityNameText = quantityName.replace(
                                /<[^>]*>/g,
                                ""
                              );
                              const MAX_NAME_LENGTH = 30;
                              const MAX_QUANTITY_LENGTH = 30;

                              const modelNameElement = (
                                <div
                                  className="modelName"
                                  dangerouslySetInnerHTML={{
                                    __html: modelName,
                                  }}
                                />
                              );
                              const quantityNameElement = (
                                <div
                                  className="quantityName"
                                  dangerouslySetInnerHTML={{
                                    __html: quantityName,
                                  }}
                                />
                              );

                              return (
                                <>
                                  {modelNameText.length > MAX_NAME_LENGTH ? (
                                    <HtmlTooltip
                                      title={
                                        <React.Fragment>
                                          <div className="customTooltip">
                                            {modelNameText}
                                          </div>
                                        </React.Fragment>
                                      }
                                    >
                                      {modelNameElement}
                                    </HtmlTooltip>
                                  ) : (
                                    modelNameElement
                                  )}
                                  {quantityNameText.length >
                                  MAX_QUANTITY_LENGTH ? (
                                    <HtmlTooltip
                                      title={
                                        <React.Fragment>
                                          <div className="customTooltip">
                                            {quantityNameText}
                                          </div>
                                        </React.Fragment>
                                      }
                                    >
                                      {quantityNameElement}
                                    </HtmlTooltip>
                                  ) : (
                                    quantityNameElement
                                  )}
                                </>
                              );
                            } else {
                              // Fallback if no colon is found
                              const nameText = dist.name.replace(
                                /<[^>]*>/g,
                                ""
                              );
                              const MAX_NAME_LENGTH = 30;
                              const nameElement = (
                                <div
                                  className="modelName"
                                  dangerouslySetInnerHTML={{
                                    __html: dist.name,
                                  }}
                                />
                              );

                              return nameText.length > MAX_NAME_LENGTH ? (
                                <HtmlTooltip
                                  title={
                                    <React.Fragment>
                                      <div className="customTooltip">
                                        {nameText}
                                      </div>
                                    </React.Fragment>
                                  }
                                >
                                  {nameElement}
                                </HtmlTooltip>
                              ) : (
                                nameElement
                              );
                            }
                          })()}
                        </div>
                      ) : (
                        (() => {
                          const nameText = dist.name.replace(/<[^>]*>/g, "");
                          const MAX_NAME_LENGTH = 30;
                          const nameElement = (
                            <div
                              className="distListEntryName"
                              dangerouslySetInnerHTML={{ __html: dist.name }}
                            />
                          );

                          return nameText.length > MAX_NAME_LENGTH ? (
                            <HtmlTooltip
                              title={
                                <React.Fragment>
                                  <div className="customTooltip">
                                    {nameText}
                                  </div>
                                </React.Fragment>
                              }
                            >
                              {nameElement}
                            </HtmlTooltip>
                          ) : (
                            nameElement
                          );
                        })()
                      )}
                      <div
                        className="curveColorArea"
                        style={{ backgroundColor: color }}
                        onClick={(e) => handleColorClick(color, dist, e)}
                      />
                      <LineModeSelect
                        dist={dist}
                        updateLineMode={updateLineMode}
                        defaultLineMode={currentMode}
                      />
                      {currentMode !== "markers" && (
                        <>
                          <LineStyleSelect
                            updateLine={updateLine}
                            dist={dist}
                            defaultLine={currentLine}
                          />
                          <LineWidthSelect
                            updateWidth={updateWidth}
                            dist={dist}
                            defaultWidth={currentWidth}
                          />
                        </>
                      )}
                      {currentMode === "markers" && (
                        <>
                          <div></div>
                          <div></div>
                        </>
                      )}
                      {currentMode !== "lines" && (
                        <>
                          <MarkerSymbolSelect
                            dist={dist}
                            updateMarkerSymbol={updateMarkerSymbol}
                            defaultMarkerSymbol={currentMarker}
                          />
                          <MarkerSizeSelect
                            dist={dist}
                            updateMarkerSize={updateMarkerSize}
                            defaultMarkerSize={currentMarkerSize}
                          />
                        </>
                      )}
                      {currentMode === "lines" && (
                        <>
                          <div></div>
                          <div></div>
                        </>
                      )}
                      {/*isModel ? (
                        <DownloadIcon
                          className="downloadIcon"
                          onClick={() =>
                            handleDistDownload(dist.modelId, dist.quantity)
                          }
                        />
                      ) : (
                        <></>
                      )*/}
                      <img
                        className="distListEntryIcon"
                        src={deleteIcon}
                        alt={"detele icon"}
                        id="curve-entry-delete-button"
                        onClick={() => {
                          handleDistDelete(dist);
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div
              className="distList"
              ref={distListRef}
              id="dist-list-in-curves-modal"
              style={{ padding: "20px", textAlign: "center", color: "#666" }}
            >
              No curves selected.
            </div>
          )}
        </div>
        {type === "standard" ? (
          <>
            <div
              className={`distOverlay ${isDistOverlayVisible ? "visible" : ""}`}
            >
              <div
                className="distributionsModal"
                id="add-distributions-slide-in-curves-modal"
              >
                <div className="fileDistributions">
                  <div className="fileDistTitle">Add Data Curve:</div>
                  <div className="fileDistList">
                    {selectFileList.length > 0
                      ? selectFileList
                      : "No Files Available"}
                  </div>
                </div>
                <div className="distributionsDivider"></div>
                <div
                  className="modelDistributions"
                  id="add-model-curves-list-curves-modal"
                >
                  <div className="modelDistTitle" id="models-list-header">
                    Add Model Curve:
                  </div>
                  <div className="modelDistList">
                    {selectModelList.length > 0
                      ? selectModelList
                      : "No Models To Select"}
                  </div>
                </div>
              </div>

              {/* <Button
                  variant="contained"
                  size="small"
                  sx={{ m: 1 }}
                  onClick={toggleDistOverlay}
                  className="closeOverlayButton"
                  id="close-curve-slide-in-button"
                >
                  Close
                </Button> */}
            </div>
          </>
        ) : (
          <></>
        )}
        <Modal
          isOpen={colorModalOpen}
          onRequestClose={handleColorModalClose}
          shouldCloseOnOverlayClick={true}
          contentLabel="Model Modal"
          appElement={reference.current}
          style={{
            content: {
              width: "280px",
              height: "180px",
              top: `${clickCoordinates.y - 50}px`,
              left: `${clickCoordinates.x - 100}px`,
              right: "auto",
            },
            overlay: {
              backgroundColor: "transparent",
              zIndex: zIndex + 300,
            },
          }}
        >
          <ColorPicker
            oldColor={colorToChange}
            confirmNewColor={handleNewColorConfirm}
          />
        </Modal>
      </div>
    </Modal>
  );
}

export default CurvesModal;
